import { Inject, Injectable } from '@angular/core';
import { EnvironmentInstanceToken, PaceEnvironment } from '../../interfaces/environment.interface';
import { UAParser } from 'ua-parser-js';
import { isDefined } from '../../utilities/is-defined';
import { LocalStorageCommonService } from '../local-storage/local-storage-common.service';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

export const MOCK_DEVICE_ID = 'mockDeviceId';
export const DEVICE_ID_PREFIX = '[[DeviceID]]';
const DEVICE_ID_STORAGE_KEY = 'deviceId';

@Injectable({
    providedIn: 'root',
})
export class DeviceIdService {
    /**
     * USED BY PACE FORMS ONLY. Can be removed in March 2026.
     * It is used to store a device id value in pace forms context temporarily.
     */
    private legacyDeviceId: string;

    /**
     * This service doesnt need initialization anymore. It can be used by just injecting it and calling member functions.
     * Additionally, this service is no longer storing the device id in local storage, because it is not required
     * anymore. It will rather generate device ids on the fly, because the new device id algorithm is deterministic.
     * This service is used to support the session check and forms locking.
     *
     * @param log
     * @param environment
     * @param parser
     * @param localStorageCommonService
     */
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        @Inject(EnvironmentInstanceToken) private readonly environment: PaceEnvironment,
        private readonly parser: UAParser = new UAParser(),
        private readonly localStorageCommonService: LocalStorageCommonService,
    ) {}

    /**
     * NEW DEVICE ID ALGORITHM.
     * The new device id is deterministic and will have the same value for a specific user, on a specific device and
     * browser. Means, the same browser on the same device for the same user will always produce the same device id.
     * Switching one condition of it will result in a different device id. Its also prefixed to make it detectable by
     * code. May throw an error if GID is empty or undefined. PACE should not continue in this case.
     *
     * Examples:
     * [[DeviceID]]-Z1234567-macOS-Apple_Macintosh-Chrome
     * [[DeviceID]]-Z1234567-macOS-Apple_Macintosh-Safari
     * [[DeviceID]]-Z1234567-iOS-Apple_iPhone-Mobile Safari
     *
     * @param gid Required to make device ids unique per user. Passed as parameter to make it testable from outside.
     */
    public getDeviceId(gid: string): string {
        if (this.environment.operateInMockMode) {
            return MOCK_DEVICE_ID;
        }

        if (!isDefined(gid) || gid.trim().length === 0) {
            const errorMessage = `DeviceIdService: GID may not be empty for device id generation.`;
            this.log.error(errorMessage);
            throw new Error(errorMessage);
        }

        const { os, device, browser } = this.parser.getResult();
        const osId = this.joinParts(os?.name);
        const deviceId = this.joinParts(device?.vendor, device?.model);
        const browserId = this.joinParts(browser?.name);

        return `${DEVICE_ID_PREFIX}-${gid}-${osId}-${deviceId}-${browserId}`;
    }

    /**
     * USED BY PACE FORMS ONLY. Can be removed in March 2026.
     * Returns the old legacy device id, when this has been set priorly and if the stored device id also equals the
     * stored value. In all other cases, the new device id is returned.
     *
     * Example: A form is still locked after publishing the new device id algorithm using the old device it, which is
     * still stored in users local storage. To open the form, the old device id is needed. To decide whether the old or
     * the new device id should be returned, we check if the form has an old device id set and additionally compare it
     * to the old stored value.
     *
     * @param gid GID is needed in case this function needs to return the new device id.
     */
    public getLegacyDeviceId(gid: string): string {
        if (this.environment.operateInMockMode) {
            return MOCK_DEVICE_ID;
        }

        // The form was not locked, so lock it with actual device id of new algorithm.
        // If an empty value has been passed in, then we switch to the new device id algorithm to lock the form.
        if (!isDefined(this.legacyDeviceId) || this.legacyDeviceId.trim().length === 0) {
            return this.getDeviceId(gid);
        }

        // The form was already locked with device id of new algorithm.
        // If a legacy device id has been passed in, but it is detected as a new device id, then return the
        // actual device id with new algorithm to allow comparison for equalness. The form was already locked with
        // device id of new algorithm.
        if (this.legacyDeviceId.startsWith(DEVICE_ID_PREFIX)) {
            return this.getDeviceId(gid);
        }

        // The form was locked by an old device id.
        // If the device id of the form equals the device id stored in local storage, then return it to unlock the form.
        // We are sure here it is not a device id with new algorithm, so we return what we have stored.
        // If we dont have anything stored, then this will be falsy and skipped.
        const storageDeviceId = this.localStorageCommonService.storageGet<string>(DEVICE_ID_STORAGE_KEY) || '';
        if (this.legacyDeviceId === storageDeviceId) {
            return storageDeviceId;
        }

        // This user did not lock the form, we checked that just before.
        // So we return the actual device id based on the new algorithm to make clear that we want to use the new algorithm.
        return this.legacyDeviceId;
    }

    /**
     * USED BY PACE FORMS ONLY. Can be removed in March 2026.
     * The Purpose is to give this service information about the device id who locked a form.
     * This is needed to decide whether to return an old legacy device id from local storage, or a new device id using
     * os, vendor and browser information. Only required during the transition period from old legacy device id to
     * the new device id algorithm.
     *
     * @param legacyDeviceId Comes from forms-list.component#editForm() on opening a specific form.
     */
    public setLegacyDeviceId(legacyDeviceId: string): void {
        this.legacyDeviceId = legacyDeviceId;
    }

    /**
     * Removes empty, missing and undefined parameters and normalizes them to the word 'unknown'.
     * Used for a consistent, deterministic value generation and testability.
     *
     * @param parts Unlimited list of parameters to be normalized.
     * @private
     */
    private joinParts(...parts: Array<string | undefined>): string {
        const cleaned = parts.map((p) => p?.replace?.(/\s+/g, '') || '').filter(Boolean);
        return cleaned.join('_') || 'unknown';
    }
}
